import { resolveInternalStagingBaseDomain } from './internalStagingHost'

export interface SharedDomainMapping {
  tenantId: string
  type?: 'admin' | 'user' | 'api'
  name?: string
  tier?: string
  verified?: boolean
  isPlatform?: boolean
  isInternalStagingHost?: boolean
}

export interface SharedDomainMappingsOptions {
  cacheKey: string
  urls: string[]
  localMappings?: Record<string, SharedDomainMapping>
  ttlMs?: number
  debug?: boolean
  cacheEmptyResults?: boolean
}

type SharedDomainMappingsCacheEntry = {
  timestamp: number
  data: Record<string, SharedDomainMapping>
}

const sharedDomainMappingsCache = new Map<string, SharedDomainMappingsCacheEntry>()
const DOMAIN_MAPPINGS_FETCH_TIMEOUT_MS = 5_000
const DOMAIN_MAPPINGS_RATE_LIMIT_BACKOFF_MS = 120_000

const domainMappingsInFlight = new Map<string, Promise<Record<string, SharedDomainMapping>>>()
const domainMappingsRateLimitedUntil = new Map<string, number>()

const fetchWithTimeout = async (url: string, timeoutMs: number): Promise<Response> => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    return await fetch(url, { signal: controller.signal })
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export const isLocalDevelopmentHost = (hostname: string): boolean => {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('127.0.0.') ||
    hostname.includes('localhost')
  )
}

export const normalizeDomainHost = (host: string): string => {
  return host.trim().toLowerCase()
}

const buildGeneratedTestTenantId = (rawNumber: string): string => {
  const parsed = Number.parseInt(rawNumber, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return ''
  return `test-demo-${String(parsed).padStart(2, '0')}`
}

export const inferGeneratedTenantIdFromHost = (host: string): string => {
  const normalizedHost = normalizeDomainHost(host)
  const hostnameOnly = normalizeDomainHost(normalizedHost.split(':')[0] || normalizedHost)
  const subdomain = hostnameOnly.split('.')[0] || ''

  if (subdomain === 'ag168') {
    return 'test-demo-01'
  }

  const agTestMatch = subdomain.match(/^ag168t(\d+)$/)
  if (agTestMatch) {
    return buildGeneratedTestTenantId(agTestMatch[1] || '')
  }

  const testMatch = subdomain.match(/^test(\d+)$/)
  if (testMatch) {
    return buildGeneratedTestTenantId(testMatch[1] || '')
  }

  return ''
}

export const buildSimpleLocalMappings = (
  mappings: Record<string, string>,
  defaults: Omit<SharedDomainMapping, 'tenantId'> = {}
): Record<string, SharedDomainMapping> => {
  const result: Record<string, SharedDomainMapping> = {}

  Object.entries(mappings).forEach(([domain, tenantId]) => {
    if (!domain || !tenantId) return
    result[normalizeDomainHost(domain)] = {
      tenantId,
      verified: true,
      ...defaults,
    }
  })

  return result
}


export const resolveDomainMappingForHost = (
  mappings: Record<string, SharedDomainMapping>,
  host: string
): SharedDomainMapping | null => {
  const normalizedHost = normalizeDomainHost(host)
  const hostnameOnly = normalizeDomainHost(normalizedHost.split(':')[0] || normalizedHost)
  const bareHost = hostnameOnly.replace(/^www\./, '')
  const candidates = Array.from(new Set([normalizedHost, hostnameOnly, bareHost]))

  for (const candidate of candidates) {
    if (candidate && mappings[candidate]) {
      return mappings[candidate]
    }
  }

  const baseDomain = resolveInternalStagingBaseDomain(bareHost)
  if (baseDomain) {
    const baseMapping = mappings[baseDomain]
    if (baseMapping) {
      return { ...baseMapping, isInternalStagingHost: true }
    }
  }

  return null
}

const fetchSharedDomainMappingsOnce = async (
  options: SharedDomainMappingsOptions
): Promise<Record<string, SharedDomainMapping>> => {
  const {
    urls,
    localMappings = {},
    debug = false,
    cacheKey,
  } = options

  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
  if (hostname && isLocalDevelopmentHost(hostname)) {
    if (debug) console.log('📦 Localhost: using local domain mappings')
    return localMappings
  }

  const backoffUntil = domainMappingsRateLimitedUntil.get(cacheKey) || 0
  if (Date.now() < backoffUntil) {
    if (debug) console.warn('⚠️ domain-mappings in rate-limit backoff, using local fallback')
    return localMappings
  }

  for (const url of urls) {
    if (!url) continue

    try {
      const response = await fetchWithTimeout(url, DOMAIN_MAPPINGS_FETCH_TIMEOUT_MS)
      if (response.status === 429) {
        domainMappingsRateLimitedUntil.set(cacheKey, Date.now() + DOMAIN_MAPPINGS_RATE_LIMIT_BACKOFF_MS)
        if (debug) console.warn(`⚠️ domain-mappings rate limited (${url}) — backing off`)
        break
      }
      if (!response.ok) {
        if (debug) console.warn(`⚠️ domain-mappings ${url} -> ${response.status}`)
        continue
      }

      const data = await response.json()
      if (!data?.success) continue

      const rawMappings = data?.data?.mappings || {}
      const mappings: Record<string, SharedDomainMapping> = {}

      Object.entries(rawMappings).forEach(([domain, mapping]) => {
        const value = mapping as SharedDomainMapping
        mappings[normalizeDomainHost(domain)] = {
          ...value,
          verified: value.verified ?? true,
        }
      })

      if (debug) {
        console.log(`✅ Domain mappings loaded from ${url}:`, Object.keys(mappings).length, 'entries')
      }
      return mappings
    } catch (error) {
      if (debug) console.warn(`⚠️ domain-mappings fetch error (${url}):`, error)
    }
  }

  if (debug) {
    console.warn('⚠️ All domain-mappings endpoints failed, using local fallback')
  }
  return localMappings
}

export const fetchSharedDomainMappings = async (
  options: SharedDomainMappingsOptions
): Promise<Record<string, SharedDomainMapping>> => {
  const inFlightKey = options.cacheKey
  const existing = domainMappingsInFlight.get(inFlightKey)
  if (existing) return existing

  const request = fetchSharedDomainMappingsOnce(options).finally(() => {
    domainMappingsInFlight.delete(inFlightKey)
  })
  domainMappingsInFlight.set(inFlightKey, request)
  return request
}

export const getSharedDomainMappings = async (
  options: SharedDomainMappingsOptions
): Promise<Record<string, SharedDomainMapping>> => {
  const ttlMs = options.ttlMs ?? 5 * 60 * 1000
  const cacheEntry = sharedDomainMappingsCache.get(options.cacheKey)
  const now = Date.now()

  if (cacheEntry && now - cacheEntry.timestamp < ttlMs) {
    if (options.debug) console.log('📦 Using cached domain mappings')
    return cacheEntry.data
  }

  const data = await fetchSharedDomainMappings(options)
  const shouldCache = options.cacheEmptyResults === true || Object.keys(data).length > 0
  if (shouldCache) {
    sharedDomainMappingsCache.set(options.cacheKey, { timestamp: now, data })
    return data
  }

  if (cacheEntry) {
    if (options.debug) console.warn('⚠️ Domain mappings fetch empty; keeping previous cache')
    return cacheEntry.data
  }

  if (options.debug) {
    console.warn('⚠️ Domain mappings result empty; skipping cache write')
  }
  return data
}

export const clearSharedDomainMappingsCache = (cacheKey: string): void => {
  sharedDomainMappingsCache.delete(cacheKey)
}
